import { useState } from "react";
import { X, KeyRound, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Modal Lupa Password — self-service reset via NIK verification.
 * User isi email + 6 digit terakhir NIK. Backend reset ke NIK last6
 * + set must_change_password=true. Response selalu generic (tidak bocorkan info).
 */
export default function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [nik, setNik] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {message} setelah submit

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !nik) return;
    if (nik.length !== 6 || !/^\d{6}$/.test(nik)) {
      setResult({ error: "NIK harus 6 digit terakhir angka" });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim(), nik: nik.trim() });
      setResult({ message: data?.message || "Kalau data cocok, password telah di-reset. Silakan login." });
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Gagal memproses. Coba lagi." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="forgot-password-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 grid place-items-center">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-lg">Lupa Password?</h3>
              <p className="text-[11px] text-emerald-100 mt-0.5">Reset password ke 6 digit terakhir NIK</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="forgot-close" className="text-white/70 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {result?.message ? (
          <div className="p-6 text-center" data-testid="forgot-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-sm text-slate-800 font-medium mb-4">{result.message}</p>
            <button onClick={onClose} data-testid="forgot-back-to-login"
              className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm">
              Kembali ke Login
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Cara kerja:</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Isi email login kamu</li>
                <li>Isi 6 digit terakhir NIK (Nomor Induk Karyawan)</li>
                <li>Kalau cocok, password otomatis di-reset ke NIK 6 digit itu</li>
                <li>Login ulang → sistem akan minta kamu ganti password segera</li>
                {/* Backend menolak reset mandiri untuk akun selain karyawan (keputusan
                    owner 18 Sep 2026) dengan pesan generik — ditulis di sini supaya
                    pimpinan tidak menebak-nebak kenapa passwordnya tidak berubah. */}
                <li>Hanya untuk akun karyawan — akun admin/pimpinan hubungi Bos (superadmin)</li>
              </ol>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                data-testid="forgot-email"
                placeholder="you@zoagroup.com"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600" />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">6 Digit Terakhir NIK</label>
              <input type="text" required value={nik} maxLength={6}
                onChange={e => setNik(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="forgot-nik"
                placeholder="123456"
                inputMode="numeric"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600" />
              <p className="text-[10px] text-slate-400 mt-1">Tanya HR kalau lupa NIK kamu.</p>
            </div>

            {result?.error && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{result.error}</span>
              </div>
            )}

            <button type="submit" disabled={loading || !email || nik.length !== 6}
              data-testid="forgot-submit"
              className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Reset Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
